import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface AdminStatsOverview {
  totalLeads: number;
  leadsToday: number;
  leadsThisWeek: number;
  totalEmployees: number;
  jobsToday: number;
  converted: number;
}

export interface AdminEmployeeStat {
  id: string;
  display_name: string;
  username: string;
  active: number;
  role: string;
  total_leads: number;
  leads_today: number;
  leads_week: number;
  converted: number;
  contacted: number;
  total_jobs: number;
  last_active: string | null;
}

export interface StatusBreakdownEntry {
  status: string;
  n: number;
}

export interface DailyActivityEntry {
  day: string;
  n: number;
}

export interface AdminStatsResponse {
  overview: AdminStatsOverview;
  isSuperAdmin: boolean;
  byEmployee?: AdminEmployeeStat[];
  statusBreakdown?: StatusBreakdownEntry[];
  dailyActivity?: DailyActivityEntry[];
  runningJobs?: any[];
}

const fetchAdminStats = async (): Promise<AdminStatsResponse> => {
  const { data } = await api.get<AdminStatsResponse>('/api/admin/stats');
  return data;
};

/**
 * Shared central query for all Admin Dashboard KPIs, charts and analytics.
 * Fetches once and is shared (deduped) across every admin widget via the
 * `['adminStats']` key. A single `refetchInterval` drives the 60s poll that
 * matches the legacy `loadDashboard()` cadence (public/app.js) — one timer,
 * one request, no per-widget timers.
 */
export function useAdminStats<TData = AdminStatsResponse>(
  select?: (data: AdminStatsResponse) => TData
): UseQueryResult<TData, Error> {
  return useQuery({
    queryKey: ['adminStats'],
    queryFn: fetchAdminStats,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 60 * 1000,
    select,
  });
}

// Selectors for individual widgets to prevent duplicate mapping logic
export const selectTotalLeads = (data: AdminStatsResponse) => data.overview?.totalLeads ?? 0;
export const selectLeadsToday = (data: AdminStatsResponse) => data.overview?.leadsToday ?? 0;
export const selectLeadsThisWeek = (data: AdminStatsResponse) => data.overview?.leadsThisWeek ?? 0;
export const selectActiveEmployees = (data: AdminStatsResponse) => data.overview?.totalEmployees ?? 0;
export const selectJobsToday = (data: AdminStatsResponse) => data.overview?.jobsToday ?? 0;
export const selectConverted = (data: AdminStatsResponse) => data.overview?.converted ?? 0;

export const selectLeadVelocity = (data: AdminStatsResponse) => data.dailyActivity || [];
export const selectPipelineHealth = (data: AdminStatsResponse) => data.statusBreakdown || [];
export const selectTeamLeaderboard = (data: AdminStatsResponse) => data.byEmployee || [];
